/**
 * Professional AutoComplete Component
 * Features: Debouncing, Keyboard Navigation, Caching, Error Handling
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { 
  searchInstitutions, 
  searchCountries, 
  University, 
  Country,
  AutoSuggestResult 
} from '../services/educationApi';

export interface AutoCompleteProps {
  // Basic props
  value: string;
  onChange: (value: string, selectedItem?: University | Country) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  
  // AutoComplete specific
  type: 'institution' | 'country';
  country?: string; // For institution search filtering
  limit?: number;
  minLength?: number;
  
  // Styling
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  
  // Callbacks
  onSelect?: (item: University | Country) => void;
  onError?: (error: string) => void;
  onSearch?: (query: string) => void;
}

interface AutoCompleteState {
  isOpen: boolean;
  suggestions: (University | Country)[];
  loading: boolean;
  error: string | null;
  highlightedIndex: number;
  lastSearchQuery: string;
  cached: boolean;
}

const AutoComplete: React.FC<AutoCompleteProps> = ({
  value,
  onChange,
  placeholder = "Start typing...",
  disabled = false,
  required = false,
  error,
  type,
  country,
  limit = 10,
  minLength = 2,
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  onSelect,
  onError,
  onSearch
}) => {
  // State management
  const [state, setState] = useState<AutoCompleteState>({
    isOpen: false,
    suggestions: [],
    loading: false,
    error: null,
    highlightedIndex: -1,
    lastSearchQuery: '',
    cached: false
  });

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Search function
  const performSearch = useCallback(async (query: string) => {
    if (!query || query.trim().length < minLength) {
      setState(prev => ({
        ...prev,
        suggestions: [],
        isOpen: false,
        loading: false,
        error: null
      }));
      return;
    }

    const normalizedQuery = query.trim();
    if (normalizedQuery === state.lastSearchQuery) {
      return; // Skip duplicate searches
    }

    setState(prev => ({ ...prev, loading: true, error: null, lastSearchQuery: normalizedQuery }));
    onSearch?.(normalizedQuery);

    try {
      let results: (University | Country)[] = [];
      let cached = false;

      if (type === 'institution') {
        const response: AutoSuggestResult = await searchInstitutions(normalizedQuery, country, limit);
        results = response.institutions;
        cached = response.cached;
      } else if (type === 'country') {
        results = await searchCountries(normalizedQuery);
      }

      setState(prev => ({
        ...prev,
        suggestions: results,
        isOpen: results.length > 0,
        loading: false,
        error: null,
        highlightedIndex: -1,
        cached
      }));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Search failed';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
        suggestions: [],
        isOpen: false
      }));
      onError?.(errorMessage);
    }
  }, [type, country, limit, minLength, state.lastSearchQuery, onSearch, onError]);

  // Debounced search
  const debouncedSearch = useCallback((query: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  }, [performSearch]);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    if (newValue !== state.lastSearchQuery) {
      debouncedSearch(newValue);
    }
  }, [onChange, debouncedSearch, state.lastSearchQuery]);

  // Handle suggestion selection
  const handleSelect = useCallback((item: University | Country, index?: number) => {
    const selectedValue = item.name;
    
    onChange(selectedValue, item);
    onSelect?.(item);
    
    setState(prev => ({
      ...prev,
      isOpen: false,
      highlightedIndex: -1,
      suggestions: []
    }));
    
    inputRef.current?.blur();
  }, [onChange, onSelect]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!state.isOpen) {
      if (e.key === 'ArrowDown' && state.suggestions.length > 0) {
        setState(prev => ({ ...prev, isOpen: true, highlightedIndex: 0 }));
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setState(prev => ({
          ...prev,
          highlightedIndex: Math.min(prev.highlightedIndex + 1, prev.suggestions.length - 1)
        }));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setState(prev => ({
          ...prev,
          highlightedIndex: Math.max(prev.highlightedIndex - 1, -1)
        }));
        break;

      case 'Enter':
        e.preventDefault();
        if (state.highlightedIndex >= 0 && state.suggestions[state.highlightedIndex]) {
          handleSelect(state.suggestions[state.highlightedIndex], state.highlightedIndex);
        }
        break;

      case 'Escape':
        setState(prev => ({ ...prev, isOpen: false, highlightedIndex: -1 }));
        inputRef.current?.blur();
        break;

      case 'Tab':
        setState(prev => ({ ...prev, isOpen: false, highlightedIndex: -1 }));
        break;
    }
  }, [state.isOpen, state.highlightedIndex, state.suggestions, handleSelect]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setState(prev => ({ ...prev, isOpen: false, highlightedIndex: -1 }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (state.highlightedIndex >= 0 && suggestionRefs.current[state.highlightedIndex]) {
      suggestionRefs.current[state.highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [state.highlightedIndex]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Render suggestion item
  const renderSuggestion = (item: University | Country, index: number) => {
    const isHighlighted = index === state.highlightedIndex;
    const isUniversity = 'domains' in item;
    
    return (
      <div
        key={`${item.name}-${index}`}
        ref={el => { suggestionRefs.current[index] = el; }}
        className={`
          px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0
          ${isHighlighted ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
          transition-colors duration-150
        `}
        onClick={() => handleSelect(item, index)}
        onMouseEnter={() => setState(prev => ({ ...prev, highlightedIndex: index }))}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="font-medium text-sm">
              {item.name}
            </div>
            {isUniversity && (item as University).country && (
              <div className="text-xs text-gray-500 mt-1">
                <GlobeAltIcon className="w-3 h-3 inline mr-1" />
                {(item as University).country}
              </div>
            )}
            {'flag' in item && item.flag && (
              <div className="text-xs text-gray-500 mt-1">
                {item.flag} {item.name}
              </div>
            )}
          </div>
          {isHighlighted && (
            <CheckIcon className="w-4 h-4 text-blue-600 ml-2" />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (state.suggestions.length > 0) {
              setState(prev => ({ ...prev, isOpen: true }));
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`
            w-full px-4 py-2 pr-10 border rounded-md
            ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
            focus:outline-none focus:ring-2 focus:ring-opacity-50
            transition-colors duration-200
            ${inputClassName}
          `}
        />
        
        {/* Loading/Search Icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {state.loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          ) : state.error ? (
            <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
          ) : (
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Error Message */}
      {(error || state.error) && (
        <p className="mt-1 text-sm text-red-600">
          {error || state.error}
        </p>
      )}

      {/* Dropdown */}
      {state.isOpen && state.suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className={`
            absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg
            max-h-60 overflow-y-auto
            ${dropdownClassName}
          `}
        >
          {/* Cache indicator */}
          {state.cached && (
            <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-green-700 text-xs">
              <CheckIcon className="w-3 h-3 inline mr-1" />
              Cached results
            </div>
          )}
          
          {/* Suggestions */}
          {state.suggestions.map((item, index) => renderSuggestion(item, index))}
          
          {/* Results count */}
          {state.suggestions.length >= limit && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-gray-500 text-xs text-center">
              Showing top {limit} results
            </div>
          )}
        </div>
      )}

      {/* No results message */}
      {state.isOpen && !state.loading && state.suggestions.length === 0 && value.length >= minLength && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-4 text-center text-gray-500">
          No {type === 'institution' ? 'institutions' : 'countries'} found
        </div>
      )}
    </div>
  );
};

export default AutoComplete;
